import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import NoResults from 'src/components/NoResults';
import { useSnackbar } from 'notistack';
import { createAxiosInstance } from 'src/utils/axios';
import CircularLoader from 'src/components/CircularLoader';
import PurchaseOrderService from 'src/services/PurchaseOrderService';
import SecondaryChargeInfo from './cards/SecondaryChargeInfo';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
  },
  contentBody: {
    marginTop: '-5px',
    height: '100%',
    [theme.breakpoints.up('lg')]: {
      width: 'calc(100vw - 330px)',
      paddingRight: '50px',
    },
  },
  noResultsContainer: {
    marginTop: '20%',
  },
  loadingContainer: {
    marginTop: '30%',
  },
  purchaseOrderSubHeaderValue: {
    color: theme.palette.secondary.main,
    paddingLeft: '15px',
    paddingRight: '10px',
    paddingBottom: '15px',
  },
}));

const purchaseOrderService = new PurchaseOrderService(
  createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);

function PurchaseOrderSecondaryChargesDetail({ 
  poNumber,
  showProviderCharges,
  isReadOnly,
  setIsDirty,
  bodyStyle,
  title,
  onSave,
}) {

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState(null);
  const [purchaseOrderChargeTypes, setPurchaseOrderChargeTypes] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    (async () => {
      await Promise.all([
        getPurchaseOrderDetails(poNumber),
        getPurchaseOrderChargeTypes()
      ]);
    })();
  }, [poNumber]);

  async function getPurchaseOrderDetails(id) {
    try {
      setIsLoading(true);
      setIsError(false);

      const res = await purchaseOrderService.getPurchaseOrderDetails(id);
      const data = res.data.data;

      data.isDirty = false;
      setIsDirty(false);

      setPurchaseOrderDetails(data);
    } catch (error) {
      enqueueSnackbar('Error fetching purchase order details', { variant: 'error' });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function getPurchaseOrderChargeTypes() {
    try {
      setIsLoading(true);
      setIsError(false);

      const res = await purchaseOrderService.getPurchaseOrderChargeTypes();
      setPurchaseOrderChargeTypes(res.data.purchaseOrderChargeTypes);
    } catch (error) {
      enqueueSnackbar('Error fetching purchase order charge types', { variant: 'error' });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSavePurchaseOrder() {
    try {
      setIsLoading(true);
      setIsError(false);

      const charges = purchaseOrderDetails.Charges.map(o => ({
        purchaseOrderChargeId: Number(o.purchaseOrderChargeId),
        purchaseOrderChargeTypeId: Number(o.purchaseOrderChargeTypeId),
        amount: Number(o.amount),
      }));

      const res = await purchaseOrderService.updatePurchaseOrder(
        poNumber,
        purchaseOrderDetails.MilesUnloaded,
        purchaseOrderDetails.FreeUnloadedMiles,
        purchaseOrderDetails.UnloadedMileageRate,
        purchaseOrderDetails.MilesLoaded,
        purchaseOrderDetails.FreeLoadedMiles,
        purchaseOrderDetails.LoadedMileageRate,
        purchaseOrderDetails.StorageDays,
        purchaseOrderDetails.StorageDailyRate,
        purchaseOrderDetails.SecondaryStorageDays,
        purchaseOrderDetails.SecondaryStorageDailyRate,
        purchaseOrderDetails.CreditCardFeePercent,
        charges
      );

      const updated = res.data.data;
      updated.isDirty = false;

      setIsDirty(false);
      setPurchaseOrderDetails(updated);

      return updated;
    } catch (error) {
      enqueueSnackbar('Error updating purchase order', { variant: 'error' });
      setIsError(true);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  async function onSaveHandler() {
    try {
      const updatedPoDetails = await handleSavePurchaseOrder();
      enqueueSnackbar('Purchase Order saved successfully');

      if (typeof onSave === 'function') {
        onSave(updatedPoDetails);
      }
    } catch (error) {
      enqueueSnackbar('Failed to save purchase order', { variant: 'error' });
    }
  }

  function isNullOrEmpty(str) {
    return !str || str.trim() === '';
  }

  if (isError) {
    return (
      <div className={classes.noResultsContainer} title="Error loading details">
        <NoResults />
      </div>
    );
  }

  if (isLoading || !purchaseOrderDetails) {
    return (
      <div className={classes.loadingContainer}>
        <CircularLoader />
      </div>
    );
  }

  return (
    <main className={ classes.root}>
      <div className={bodyStyle ?? classes.contentBody}>
        <Grid item xs={12}>
          <Typography variant="h3" className={classes.purchaseOrderSubHeaderValue}>
            {isNullOrEmpty(title)
              ? `PO ${purchaseOrderDetails.PoNumber} Secondary Charge Summary`
              : title}
          </Typography>

          <SecondaryChargeInfo
            purchaseOrderDetails={purchaseOrderDetails}
            setPurchaseOrderDetails={setPurchaseOrderDetails}
            chargeFees={purchaseOrderDetails.Charges}
            chargeTypes={purchaseOrderChargeTypes}
            isReadOnly={isReadOnly}
            setIsDirty={setIsDirty}
            showProviderCharges={showProviderCharges}
            onSave={onSaveHandler}
          />
        </Grid>
      </div>
    </main>
  );
}

PurchaseOrderSecondaryChargesDetail.propTypes = {
  poNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  showProviderCharges: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  setIsDirty: PropTypes.func.isRequired,
  title: PropTypes.string,
  onSave: PropTypes.func,
};

PurchaseOrderSecondaryChargesDetail.defaultProps = {
  showProviderCharges: false,
  isReadOnly: true,
};

export default PurchaseOrderSecondaryChargesDetail;
